/**
 * Admin-side API client. Wraps fetch with Clerk session credentials.
 *
 * Clerk's React SDK auto-attaches session cookies to same-origin fetches when
 * `credentials: 'include'` is set, so this is transport-layer thin.
 */

const apiBase = (): string => {
  const override = import.meta.env.VITE_API_BASE_URL;
  if (override) return override;
  return "";
};

export type AdminSessionSummary = {
  id: string;
  subjectName: string;
  subjectEmail: string;
  interviewType: "small_business_discovery" | "expertise_capture" | "custom";
  status: "draft" | "provisioned" | "activated" | "in_progress" | "completed" | "abandoned";
  createdAt: string;
  activatedAt: string | null;
  completedAt: string | null;
  currentQuestionCount: number;
  maxQuestions: number;
  code: string;
};

export type AdminSessionDetail = {
  session: AdminSessionSummary & {
    topicContext: string;
    language: string | null;
    finalMdBlobUrl: string | null;
    notes: string | null;
  };
  questions: {
    id: string;
    orderIndex: number;
    text: string;
    createdAt: string;
  }[];
  answers: {
    id: string;
    questionId: string;
    transcriptText: string;
    typed: boolean;
    audioBlobUrl: string | null;
    languageDetected: string | null;
    languageMismatch: boolean;
  }[];
};

export type CreateSessionBody = {
  subject_name: string;
  subject_email: string;
  interview_type: AdminSessionSummary["interviewType"];
  topic_context: string;
  language?: string | null;
  max_questions?: number;
};

export type CreateSessionResult =
  | { ok: true; id: string; code: string; link: string }
  | { ok: false; status: number; error: string };

async function authedFetch(input: string, init?: RequestInit): Promise<Response> {
  return fetch(`${apiBase()}${input}`, {
    ...init,
    credentials: "include",
    headers: {
      "content-type": "application/json",
      ...(init?.headers ?? {}),
    },
  });
}

export async function createSession(body: CreateSessionBody): Promise<CreateSessionResult> {
  const res = await authedFetch("/api/admin/sessions", {
    method: "POST",
    body: JSON.stringify(body),
  });
  if (res.ok) {
    const data = (await res.json()) as { id: string; code: string; link: string };
    return { ok: true, ...data };
  }
  let error = "unknown_error";
  try {
    const data = (await res.json()) as { error?: string };
    if (data.error) error = data.error;
  } catch {
    // ignore
  }
  return { ok: false, status: res.status, error };
}

export async function listSessions(): Promise<AdminSessionSummary[]> {
  const res = await authedFetch("/api/admin/sessions");
  if (!res.ok) return [];
  const data = (await res.json()) as { sessions: AdminSessionSummary[] };
  return data.sessions ?? [];
}

export async function getSessionDetail(id: string): Promise<AdminSessionDetail | null> {
  const res = await authedFetch(`/api/admin/sessions/${id}`);
  if (!res.ok) return null;
  return (await res.json()) as AdminSessionDetail;
}
